/**
 * The provenance drawer. Six blocks, same order every time:
 *   Value → Formula → Assumptions used → Sources → Confidence and caveats → Actions
 *
 * The formula's identifiers are chips that open THAT input's own drawer, so
 * provenance is transitively navigable rather than a flat list at the bottom.
 */

import { parseCached, stepsFor } from '../engine/evaluate.js';
import { printTokens, type Token } from '../engine/expr.js';
import { groupSourcesByInput, provenanceOf } from '../engine/provenance.js';
import { evaluateRange } from '../engine/range.js';
import type { Input, Metric, Pack, Range, Source } from '../engine/schema.js';
import { isRange, isUndisclosed } from '../engine/schema.js';
import { buildScope, inputById } from '../engine/scope.js';
import { formatValue, roundToSigFigs, unitAbbr } from '../engine/units.js';
import { esc, formatRange } from './num.js';
import type { AppState } from './store.js';

export interface DrawerContext {
  pack: Pack;
  state: AppState;
}

export function renderDrawer(ctx: DrawerContext, targetId: string): string {
  const { pack } = ctx;
  const metric = pack.metrics.find((m) => m.id === targetId);
  if (metric) return metricDrawer(ctx, metric);
  const input = pack.inputs.find((i) => i.id === targetId);
  if (input) return inputDrawer(ctx, input);
  return '';
}

function shell(title: string, sub: string, body: string): string {
  return `<div class="drawer-backdrop" data-close-drawer></div>
  <aside class="drawer" role="dialog" aria-modal="true" aria-label="${esc(title)}" tabindex="-1">
    <div class="drawer-head">
      <div>
        <span class="eyebrow">${esc(sub)}</span>
        <h3>${esc(title)}</h3>
      </div>
      <button class="drawer-close" data-close-drawer aria-label="Close">&times;</button>
    </div>
    ${body}
  </aside>`;
}

// ---------------------------------------------------------------- metric

function metricDrawer(ctx: DrawerContext, metric: Metric): string {
  const { pack, state } = ctx;
  const scopeState = {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  };
  const scope = buildScope(pack, scopeState);
  const node = parseCached(metric.expr);
  const prov = provenanceOf(node, pack);
  const byId = inputById(pack);
  const labelFor = (id: string) =>
    byId[id]?.[state.mode === 'plain' ? 'plainLabel' : 'label'] ?? id;

  const blocked = prov.inputIds.filter((id) => scope.undisclosed[id]);

  // ---- 1. value
  let valueBlock: string;
  if (blocked.length) {
    const u = scope.undisclosed[blocked[0]!]!;
    valueBlock = `<p class="figure-blocked">Not publicly disclosed. ${esc(u.note)}</p>`;
  } else {
    const value = evaluateRange(node, scope.values);
    const others = pack.scenarios
      .filter((s) => s.id !== state.scenarioId)
      .filter((s) => !metric.appliesTo || metric.appliesTo.includes(s.id))
      .map((s) => {
        const other = buildScope(pack, { ...scopeState, scenarioId: s.id });
        if (prov.inputIds.some((id) => other.undisclosed[id])) return '';
        const v = evaluateRange(node, other.values);
        return `<li><span>${esc(s.label)}</span><span>${esc(
          formatValue(roundToSigFigs(v.mid, prov.sigFigs), metric.unit),
        )}</span></li>`;
      })
      .filter(Boolean)
      .join('');
    valueBlock = `<p class="mono" style="font-size:15px">${esc(
      formatRange(value, metric.unit, prov.sigFigs),
    )} <span style="color:var(--ink-faint)">${esc(unitAbbr(metric.unit))}</span></p>
      ${others ? `<ul class="step-list">${others}</ul>` : ''}`;
  }

  // ---- 2. formula
  const tokens = printTokens(node, {
    labels: Object.fromEntries(prov.inputIds.map((id) => [id, labelFor(id)])),
  });
  const substituted = printTokens(node, {
    labels: Object.fromEntries(
      prov.inputIds.map((id) => {
        const v = scope.values[id];
        if (v === undefined) return [id, 'not disclosed'];
        const input = byId[id];
        const unit = input?.unit ?? 'dimensionless';
        return [
          id,
          isRange(v)
            ? `${formatValue(v.mid, unit)}`
            : formatValue(v, unit),
        ];
      }),
    ),
  });

  const steps = blocked.length ? [] : stepsFor(metric, pack, scopeState, labelFor);
  const stepList = steps.length
    ? `<ul class="step-list">${steps
        .map(
          (s) =>
            `<li><span>${esc(s.label)}</span><span>${esc(
              formatValue(roundToSigFigs(s.value.mid, prov.sigFigs), s.unit),
            )}</span></li>`,
        )
        .join('')}</ul>`
    : '';

  const formulaBlock = `
    <div class="formula">${renderTokens(tokens)}</div>
    <div class="formula-result">${renderTokens(substituted, false)}</div>
    ${stepList}`;

  // ---- 3. assumptions
  const assumptions = prov.assumptionIds
    .map((id) => byId[id])
    .filter((i): i is Input => Boolean(i))
    .map((i) => assumptionControl(i, ctx))
    .join('');

  // ---- 4. sources
  const groups = groupSourcesByInput(prov, pack);
  const sourcesBlock = groups
    .map(
      (g) => `<div class="source-group">
        <div class="source-group-head">${esc(labelFor(g.input.id))}</div>
        ${g.sources.map(sourceItem).join('')}
      </div>`,
    )
    .join('');

  // ---- 5. confidence and caveats
  const contested = prov.inputIds
    .map((id) => byId[id])
    .filter((i): i is Input => Boolean(i?.contested?.length))
    .flatMap((i) =>
      (i.contested ?? []).map(
        (c) => `<div class="contested">
          <div class="claim">${esc(c.claim)}</div>
          <div class="counter">${esc(c.counterClaim)}</div>
        </div>`,
      ),
    )
    .join('');

  return shell(
    metric.question,
    `${metric.category} · ${prov.confidence}`,
    `
    <div class="drawer-block"><span class="eyebrow">The number</span>${valueBlock}</div>
    <div class="drawer-block"><span class="eyebrow">How it is worked out</span>${formulaBlock}</div>
    <div class="drawer-block"><span class="eyebrow">How this was done</span><p class="prose">${esc(
      metric.methodology,
    )}</p></div>
    ${
      assumptions
        ? `<div class="drawer-block"><span class="eyebrow">Assumptions you can change</span>${assumptions}</div>`
        : ''
    }
    <div class="drawer-block"><span class="eyebrow">Sources</span>${
      sourcesBlock || '<p class="prose">No sources yet — this is a placeholder.</p>'
    }</div>
    <div class="drawer-block"><span class="eyebrow">What this does not capture</span>
      <ul class="caveat-list">${metric.caveats.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>
      ${contested}
    </div>
    <div class="drawer-block drawer-actions">
      <button data-copy-citation="${esc(metric.id)}">Copy this number with its sources</button>
      <button data-copy-link>Copy a link to this view</button>
    </div>`,
  );
}

// ---------------------------------------------------------------- input

function inputDrawer(ctx: DrawerContext, input: Input): string {
  const { pack, state } = ctx;
  const scope = buildScope(pack, {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  });
  const sourceById: Record<string, Source> = {};
  for (const s of pack.sources) sourceById[s.id] = s;

  const v = scope.values[input.id];
  const valueText = isUndisclosed(input.value)
    ? `Not publicly disclosed. ${input.value.note}`
    : v === undefined
      ? 'unavailable'
      : isRange(v)
        ? formatRange(v as Range, input.unit, input.sigFigs)
        : formatValue(roundToSigFigs(v, input.sigFigs), input.unit);

  const contested = (input.contested ?? [])
    .map(
      (c) => `<div class="contested">
        <div class="claim">${esc(c.claim)}</div>
        <div class="counter">${esc(c.counterClaim)}</div>
      </div>`,
    )
    .join('');

  const usedBy = pack.metrics
    .filter((m) => provenanceOf(parseCached(m.expr), pack).inputIds.includes(input.id))
    .map((m) => `<li><span>${esc(m.label)}</span><span></span></li>`)
    .join('');

  return shell(
    state.mode === 'plain' ? input.plainLabel : input.label,
    `input · ${input.kind} · ${input.confidence}`,
    `
    <div class="drawer-block"><span class="eyebrow">Value</span>
      <p class="mono" style="font-size:15px">${esc(valueText)} <span style="color:var(--ink-faint)">${esc(
        unitAbbr(input.unit),
      )}</span></p>
      <p class="assumption-rationale">As of ${esc(input.asOf)}</p>
    </div>
    ${input.note ? `<div class="drawer-block"><span class="eyebrow">Note</span><p class="prose">${esc(input.note)}</p></div>` : ''}
    ${input.adjustable ? `<div class="drawer-block"><span class="eyebrow">You can change this</span>${assumptionControl(input, ctx)}</div>` : ''}
    <div class="drawer-block"><span class="eyebrow">Sources</span>
      ${
        input.sourceIds
          .map((id) => sourceById[id])
          .filter((s): s is Source => Boolean(s))
          .map(sourceItem)
          .join('') || '<p class="prose">No source yet — this is a placeholder.</p>'
      }
      ${contested}
    </div>
    ${usedBy ? `<div class="drawer-block"><span class="eyebrow">Used by</span><ul class="step-list">${usedBy}</ul></div>` : ''}`,
  );
}

// ---------------------------------------------------------------- pieces

function renderTokens(tokens: Token[], clickable = true): string {
  return tokens
    .map((t) => {
      if (t.kind === 'ref' && t.id) {
        return clickable
          ? `<button class="chip" data-drawer="${esc(t.id)}">${esc(t.text)}</button>`
          : `<span class="chip">${esc(t.text)}</span>`;
      }
      if (t.kind === 'op' || t.kind === 'paren' || t.kind === 'sep') {
        return `<span class="op">${esc(t.text)}</span>`;
      }
      return esc(t.text);
    })
    .join('');
}

function assumptionControl(input: Input, ctx: DrawerContext): string {
  const a = input.adjustable;
  if (!a) return '';
  const scope = buildScope(ctx.pack, {
    scenarioId: ctx.state.scenarioId,
    params: {},
    overrides: ctx.state.overrides,
  });
  const raw = scope.values[input.id];
  const current =
    ctx.state.overrides[input.id] ??
    (raw === undefined ? a.min : isRange(raw) ? raw.mid : raw);
  const modified = input.id in ctx.state.overrides;

  return `<div class="assumption ${modified ? 'modified' : ''}">
    <div class="assumption-head">
      <label for="slider-${esc(input.id)}">${esc(a.sliderLabel)}</label>
      <span class="assumption-value">${esc(
        formatValue(current, input.unit),
      )}</span>
    </div>
    <input id="slider-${esc(input.id)}" type="range"
      min="${a.min}" max="${a.max}" step="${a.step}" value="${current}"
      data-override="${esc(input.id)}" />
    <p class="assumption-rationale">${esc(a.rationale)}</p>
    ${
      modified
        ? `<button class="assumption-reset" data-reset-override="${esc(input.id)}">Back to the published value</button>`
        : ''
    }
  </div>`;
}

function sourceItem(s: Source): string {
  return `<div class="source-item">
    <a href="${esc(s.url)}" target="_blank" rel="noopener noreferrer">${esc(s.title)}</a>
    ${s.quote ? `<span class="src-quote">“${esc(s.quote)}”</span>` : ''}
    <span class="src-meta">${esc(s.publisher)}${
      s.locator ? ` · ${esc(s.locator)}` : ''
    }${s.publishedAt ? ` · published ${esc(s.publishedAt)}` : ''} · retrieved ${esc(
      s.retrievedAt,
    )}</span>
    ${s.notes ? `<span class="src-meta" style="text-transform:none;letter-spacing:0">${esc(s.notes)}</span>` : ''}
  </div>`;
}
